import React, { useState, useRef, useEffect } from 'react';
import './Correspondent.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Correspondent Component ---

export default function Correspondent() {
  const [correspondentData, setCorrespondentData] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [internationalVolunteers, setInternationalVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all correspondent data from API
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Fetch all endpoints in parallel
        const [
          correspondentResult,
          staffResult,
          volunteersResult
        ] = await Promise.all([
          apiService.fetchCorrespondent(),
          apiService.fetchStaff(),
          apiService.fetchInternationalVolunteers()
        ]);
        
        console.log('Correspondent Result:', correspondentResult);
        console.log('Staff Result:', staffResult);
        console.log('Volunteers Result:', volunteersResult);
        
        // Process correspondent data
        if (correspondentResult?.status === 'success' && correspondentResult?.data?.length > 0) {
          setCorrespondentData(correspondentResult.data[0]);
          console.log('✅ Correspondent data set successfully');
        } else {
          console.warn('⚠️ No correspondent data found');
        }
        
        // Process staff members
        if (staffResult?.status === 'success' && staffResult?.data?.length > 0) {
          setStaffMembers(staffResult.data);
          console.log('✅ Staff members set:', staffResult.data.length);
        } else {
          console.warn('⚠️ No staff members found');
        }
        
        // Process international volunteers
        if (volunteersResult?.status === 'success' && volunteersResult?.data?.length > 0) {
          setInternationalVolunteers(volunteersResult.data);
          console.log('✅ International volunteers set:', volunteersResult.data.length);
        } else {
          console.warn('⚠️ No international volunteers found');
        }
        
      } catch (err) {
        console.error('❌ Error fetching data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Helper function to render HTML content safely
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  // Helper function to parse image from JSON
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return imageData;
    }
  };

  // Clean HTML from Summernote
  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Get staff icon based on role
  const getStaffIcon = (role) => {
    if (!role) return 'fa-user-tie';
    const r = role.toLowerCase();
    if (r.includes('principal')) return 'fa-user-graduate';
    if (r.includes('headmaster')) return 'fa-chalkboard-teacher';
    if (r.includes('teacher')) return 'fa-chalkboard-teacher';
    if (r.includes('admin')) return 'fa-user-cog';
    if (r.includes('warden') || r.includes('hostel')) return 'fa-home';
    if (r.includes('sports')) return 'fa-running';
    return 'fa-user-tie';
  };

  // Get country flag icon
  const getCountryIcon = (country) => {
    if (!country) return 'fa-globe';
    const c = country.toLowerCase();
    if (c.includes('united kingdom') || c.includes('uk') || c.includes('britain')) return 'fa-flag-uk';
    if (c.includes('germany')) return 'fa-flag-germany';
    if (c.includes('denmark')) return 'fa-flag-denmark';
    if (c.includes('usa') || c.includes('united states') || c.includes('america')) return 'fa-flag-usa';
    if (c.includes('australia')) return 'fa-flag-australia';
    if (c.includes('canada')) return 'fa-flag-canada';
    if (c.includes('france')) return 'fa-flag-france';
    if (c.includes('japan')) return 'fa-flag-japan';
    if (c.includes('india')) return 'fa-flag-india';
    return 'fa-globe';
  };

  // Check if there are any active volunteers
  const hasVolunteers = internationalVolunteers.length > 0;
  const hasStaff = staffMembers.length > 0;

  return (
    <>
      <Header />
      <div className="correspondent-page devnar">
        <a className="skip-link" href="#main-correspondent">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-correspondent">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-correspondent">
            <Reveal>
              <span className="hero-badge">Our Leader</span>
              <h1>Our Correspondent</h1>
              <p className="hero-subtitle">
                {correspondentData?.title_en || correspondentData?.name || 'Mrs. Jyothi Saibaba Goud'} — the guiding force and compassionate mother 
                figure behind Devnar Foundation.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-correspondent" className="wrap page-content-correspondent">

          {/* --- Loading State --- */}
          {loading && (
            <div className="loading-container">
              <div className="loader-spinner"></div>
              <p>Loading correspondent information...</p>
            </div>
          )}

          {/* --- Error State --- */}
          {error && !loading && (
            <div className="error-container">
              <i className="fas fa-exclamation-circle"></i>
              <p>{error}</p>
              <button 
                className="btn btn-secondary" 
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-redo"></i> Try Again
              </button>
            </div>
          )}

          {/* --- Profile Section --- */}
          {!loading && !error && correspondentData && (
            <section className="correspondent-profile">
              <div className="profile-grid">
                <Reveal className="profile-image-wrapper" as="div" delay={0}>
                  <div className="profile-image">
                    <img 
                      src={getImageUrl(correspondentData.image) || 'https://devnarfoundationfortheblind.org/wp-content/uploads/2022/01/7-4-scaled.jpg'} 
                      alt={correspondentData.title_en || correspondentData.name || 'Correspondent'} 
                      loading="lazy" 
                    />
                    <div className="profile-tag">
                      <span className="tag-label">Correspondent</span>
                    </div>
                  </div>
                  <div className="profile-quick-info">
                    <div className="quick-info-item">
                      <i className="fas fa-trophy qi-icon"></i>
                      <span className="qi-text">National Award Winner</span>
                    </div>
                    <div className="quick-info-item">
                      <i className="fas fa-heart qi-icon"></i>
                      <span className="qi-text">Mother to Hundreds</span>
                    </div>
                    <div className="quick-info-item">
                      <i className="fas fa-music qi-icon"></i>
                      <span className="qi-text">Cultural Excellence</span>
                    </div>
                  </div>
                </Reveal>

                <Reveal className="profile-content" as="div" delay={80}>
                  <h2>{correspondentData.title_en || correspondentData.name || 'Mrs. Jyothi Saibaba Goud'}</h2>
                  <p className="profile-title">Correspondent</p>
                  
                  {correspondentData.description_en ? (
                    <div dangerouslySetInnerHTML={createMarkup(correspondentData.description_en)} />
                  ) : (
                    <p>No description available.</p>
                  )}
                </Reveal>
              </div>
            </section>
          )}

          {/* --- Achievements - Dynamic from Correspondent Description --- */}
          {!loading && !error && correspondentData && correspondentData.description_en && (
            <section className="correspondent-achievements">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Recognitions</span>
                <h2>Achievements & Contributions</h2>
                <div className="rule" />
              </Reveal>

              <div className="achievements-grid">
                <Reveal className="achievement-card" delay={0}>
                  <div className="achievement-icon">
                    <i className="fas fa-trophy"></i>
                  </div>
                  <h3>National Award 2008</h3>
                  <p>Received the National Award from the Vice President of India on World Disabled Day as Devnar School was adjudged the best institute for empowering the visually challenged.</p>
                </Reveal>
                <Reveal className="achievement-card" delay={60}>
                  <div className="achievement-icon">
                    <i className="fas fa-music"></i>
                  </div>
                  <h3>Cultural Excellence</h3>
                  <p>Unlimited enthusiasm in making the children excel in dance and music, bringing out their hidden talents and confidence.</p>
                </Reveal>
                <Reveal className="achievement-card" delay={120}>
                  <div className="achievement-icon">
                    <i className="fas fa-medal"></i>
                  </div>
                  <h3>Life Member</h3>
                  <p>Life member of Indian Red Cross Society, AP Branch, National Association for the Blind, and All India Women's Conference.</p>
                </Reveal>
                <Reveal className="achievement-card" delay={180}>
                  <div className="achievement-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <h3>Mother to Hundreds</h3>
                  <p>A loving and compassionate mother figure to all students, providing care, guidance, and unwavering support.</p>
                </Reveal>
              </div>
            </section>
          )}

          {/* --- Staff Section - Dynamic from API --- */}
          {hasStaff && (
            <section className="staff-section">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Our Team</span>
                <h2>Dedicated Staff</h2>
                <div className="rule" />
                <p className="section-subtitle">
                  A team of dedicated professionals working together to provide the best education 
                  and care for visually challenged students.
                </p>
              </Reveal>

              <div className="staff-grid">
                {staffMembers.map((staff, index) => {
                  const icon = getStaffIcon(staff.role);
                  const imageUrl = getImageUrl(staff.image);
                  return (
                    <Reveal key={staff.id || index} className="staff-card" delay={index * 60}>
                      {imageUrl ? (
                        <img src={imageUrl} alt={staff.name_en || staff.name} className="staff-avatar" loading="lazy" />
                      ) : (
                        <div className="staff-icon">
                          <i className={`fas ${icon}`}></i>
                        </div>
                      )}
                      <h3>{staff.name_en || staff.name}</h3>
                      <span className="staff-role">{staff.role}</span>
                      <p>{cleanHtml(staff.description_en || staff.description)}</p>
                    </Reveal>
                  );
                })}
              </div>
            </section>
          )}

          {/* --- International Volunteers - Dynamic from API --- */}
          {hasVolunteers && (
            <section className="volunteers-section">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Global Community</span>
                <h2>International Volunteers</h2>
                <div className="rule" />
                <p className="section-subtitle">
                  Every year, volunteers from around the world join us, bringing cultural exchange 
                  and enriching the learning experience of our students.
                </p>
              </Reveal>

              <div className="volunteers-grid">
                {internationalVolunteers.map((vol, index) => {
                  const icon = getCountryIcon(vol.country);
                  const imageUrl = getImageUrl(vol.image);
                  return (
                    <Reveal key={vol.id || index} className="volunteer-card" delay={index * 50}>
                      {imageUrl ? (
                        <img src={imageUrl} alt={vol.country} className="volunteer-flag-img" loading="lazy" />
                      ) : (
                        <div className="volunteer-flag">
                          <i className={`fas ${icon}`}></i>
                        </div>
                      )}
                      <h3>{vol.country}</h3>
                      <span className="volunteer-org">{vol.organization}</span>
                      <p>{cleanHtml(vol.description_en || vol.description)}</p>
                    </Reveal>
                  );
                })}
              </div>
            </section>
          )}

          {/* --- No Data Message --- */}
          {!loading && !error && !correspondentData && !hasStaff && !hasVolunteers && (
            <div className="no-data-container">
              <i className="fas fa-info-circle"></i>
              <p>No correspondent information available at the moment. Please check back later.</p>
            </div>
          )}

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-correspondent">
            <div className="cta-content">
              <span className="cta-eyebrow">Join Our Family</span>
              <h2>Be a Part of Our Community</h2>
              <p>
                Whether as a volunteer, donor, or supporter — you can be part of this 
                remarkable journey of empowering visually challenged children.
              </p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="/volunteer">
                  <i className="fas fa-hands-helping"></i> Become a Volunteer
                </a>
                <a className="btn btn-outline btn-outline-light" href="/donate">
                  <i className="fas fa-heart"></i> Support Our Mission
                </a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}